// src/Pages/NematodeMap.js

import React, { useEffect, useState, useRef } from 'react';
import ReactSlider from 'react-slider';
import './NematodeMap.css';
import Header from '../Assets/Components/Header.js';
import Footer from '../Assets/Components/Footer.js';
import { MapContainer, TileLayer, Marker, Popup, useMap, LayersControl, LayerGroup} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { EsriFeatureLayer } from '../Assets/Components/ESRIFeatures.js';
import * as XLSX from 'xlsx';
import excelFile from '../Assets/Data/NematodeMap/Bezerra_et_al_2026_Dataset.xlsx';

// Layers Control
const { Overlay } = LayersControl;

// Nematode Map
const NematodeMap = () => {

  // Map containers
  const [markers, setMarkers] = useState([]); // Map markers container
  const MapZoomController = ({ markers }) => { // Controlling map zoom based on selections
    const map = useMap();
    useEffect(() => {
      if (!map || markers.length === 0) return;
      if (markers.length === 1) { // Single point — set manually to capped zoom
        map.setView(markers[0].position, 13); // 13 value fits max zoom = 18
      } else { // Fit bounds to group, respecting max zoom
        const bounds = L.latLngBounds(markers.map(m => m.position));
        if (bounds.isValid()) {
          map.fitBounds(bounds, {
            padding: [30, 30],
            maxZoom: 18
          });
        }
      }
    }, [markers, map]);
    return null;
  };

  // Dropdown filters containers
  const [IdList, setIdList] = useState([]); // Entry ID filter containers
  const [selectedIds, setSelectedIds] = useState([]);
  const [idSearch, setIdSearch] = useState('');
  const [speciesList, setSpeciesList] = useState([]); // Species filter containers
  const [selectedSpecies, setSelectedSpecies] = useState([]);
  const [speciesSearch, setSpeciesSearch] = useState('');
  const [habitatFilters, setHabitatFilters] = useState({
    Marine: true,
    Brackish: true,
    Fresh: true
  });
  const selectedHabitats = Object.keys(habitatFilters).filter(h => habitatFilters[h]);
  const [IsDeepSeaList, setIsDeepSeaList] = useState([]); // Boolean for Deep-Sea filter containers
  const [selectedIsDeepSea, setSelectedIsDeepSea] = useState([]);  
  const [IsDescriptionList, setIsDescriptionList] = useState([]); // Boolean to select description articles
  const [selectedIsDescription, setSelectedIsDescription] = useState([]);
  const [selectedDescription, setSelectedDescription] = useState([]); // Description Article filter containers
  const [descriptionSearch, setDescriptionSearch] = useState('');
  const [DescriptionList, setDescriptionList] = useState([]);
  const [selectedSighting, setSelectedSighting] = useState([]); // Sighting Article filter containers
  const [sightingSearch, setSightingSearch] = useState('');
  const [SightingList, setSightingList] = useState([]);

  // Get text width to set dropdown-container width dynamically
  const getLongestTextWidth = (list, font = '14px NowRegular') => {
    if (!list || list.length === 0) return 100;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = font;
    const longest = list.reduce((a, b) => (a.length > b.length ? a : b), '');
    return context.measureText(longest).width + 60; // +40 for padding/checkmark spacing
  };

  // storing states to set width of .search-field
  const [idInputWidth, setIdInputWidth] = useState('98%');
  const [speciesInputWidth, setSpeciesInputWidth] = useState('98%');
  const [descriptionInputWidth, setDescriptionInputWidth] = useState('98%');
  const [sightingInputWidth, setSightingInputWidth] = useState('98%');
  
  // Dropdown content timeout containers
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownTimeouts = useRef({});

  // Depth range selection slider container
  const [depthRange, setDepthRange] = useState([0, 100]); // min and max from dataset
  const [selectedDepthRange, setSelectedDepthRange] = useState([0, 100]); // user-selected range

// Helpers for safe coordinate parsing
const parseCoords = (value) => {
  if (!value || typeof value !== 'string') return null;
  const nums = value.match(/-?\d+(\.\d+)?/g);
  if (!nums || nums.length < 2) return null;
  return [parseFloat(nums[0]), parseFloat(nums[1])];
};

const toBool = (v) =>
  v === true || v === 'TRUE' || v === 'Yes' || v === 1;

  useEffect(() => {
    // loading dataset from xlsx
    const loadExcel = async () => {
      try {
        const response = await fetch(excelFile);
        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });

        // Load the 3rd sheet (Machine_Input)
        const sheet = workbook.Sheets[workbook.SheetNames[6]];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        
        // Parsing the excel file to extract data
        const parsedMarkers = jsonData
          .map((row, index) => {
            const coordStr = row['Plotting_Coordinates'] || row['Coordinates'] || Object.values(row)[18];
            const position = parseCoords(coordStr);
            if (!position) return null;
            const depthVal = parseFloat(row['Depth_Numeric_Meters']);
            return {
              id: row['ID'] != null ? row['ID'].toString() : index.toString(), position,
              species: row['Species'] || 'Species',
              marine: toBool(row['Marine']),
              brackish: toBool(row['Brackish']),
              fresh: toBool(row['Fresh']),
              isdeepsea: row['Deep-Sea_Species'] || 'Is Deep-Sea',
              isdescription: row['Original_Description'] || 'Is Description',
              description: row['Description_Article'] || 'Description',
              sighting: row['Sighting_Reference'] || 'Reference',
              sightingLink: typeof row['Link_to_Sighting_Reference'] === 'string' && row['Link_to_Sighting_Reference'].trim() !== '' ? row['Link_to_Sighting_Reference'].trim() : null,
              coordinates: row['Coordinates_EPSG4326_DMS'] || 'Coordinates',
              location: row['Location'] || 'Location',
              country: row['Country'] || 'Country',
              depth: isNaN(depthVal) ? null : depthVal
            };
          }).filter(Boolean); // removing nulls

        // Setting markers layer with all markers
        setMarkers(parsedMarkers);
        console.log('Loaded markers:', parsedMarkers);

        // Get unique IDs from column ID in the dataset to list them on the filter dropdown
        const uniqueIds = [...new Set(parsedMarkers.map(m => m.id).filter(Boolean))].sort((a, b) => Number(a) - Number(b));
        setIdList(uniqueIds);
        const idWidth = getLongestTextWidth(uniqueIds, '14px NowRegular');
        setIdInputWidth(`${idWidth}px`);
        console.log('Unique IDs:', uniqueIds);
        console.log('IDs List State:', IdList);

        // Get unique species from column Species in the dataset to list them on the filter dropdown
        const uniqueSpecies = [...new Set(parsedMarkers.map(m => m.species).filter(Boolean))].sort();
        setSpeciesList(uniqueSpecies);
        const speciesWidth = getLongestTextWidth(uniqueSpecies, '14px NowRegular');
        setSpeciesInputWidth(`${speciesWidth}px`);
        console.log('Unique species:', uniqueSpecies);
        console.log('Species List State:', speciesList);

        // Get unique values for Is Deep-Sea Species from column Deep-Sea_Species in the dataset to list them on the filter dropdown
        const uniqueIsDeepSea = [...new Set(parsedMarkers.map(m => m.isdeepsea).filter(Boolean))].sort();
        setIsDeepSeaList(uniqueIsDeepSea);
        setSelectedIsDeepSea(uniqueIsDeepSea);
        console.log('Unique Is Deep-Sea Options:', uniqueIsDeepSea);
        console.log('Is Deep-Sea Species List State:', IsDeepSeaList);

        // Get unique values for Is Description Specimen from column Original_Description in the dataset to list them on the filter dropdown
        const uniqueIsDescription = [...new Set(parsedMarkers.map(m => m.isdescription).filter(Boolean))].sort();
        setIsDescriptionList(uniqueIsDescription);
        setSelectedIsDescription(uniqueIsDescription);
        console.log('Unique Is Description Options:', uniqueIsDescription);
        console.log('Is Description List State:', IsDescriptionList);

        // Get unique description references from column Description_Reference in the dataset to list them on the filter dropdown
        const uniqueDescription = [...new Set(parsedMarkers.map(m => m.description).filter(Boolean))].sort();
        setDescriptionList(uniqueDescription);
        const descWidth = getLongestTextWidth(uniqueDescription, '14px NowRegular');
        setDescriptionInputWidth(`${descWidth}px`);
        console.log('Unique Description References:', uniqueDescription);
        console.log('Description References List State:', DescriptionList);

        // Get unique description references from column Description_Reference in the dataset to list them on the filter dropdown
        const uniqueSighting = [...new Set(parsedMarkers.map(m => m.sighting).filter(Boolean))].sort();
        setSightingList(uniqueSighting);
        const sightingWidth = getLongestTextWidth(uniqueSighting, '14px NowRegular');
        setSightingInputWidth(`${sightingWidth}px`);
        console.log('Unique Sightings:', uniqueSighting);
        console.log('Sightings List State:', SightingList);

        // Get valid numeric depths from Depth_Meters in the dataset to create depth range selection slider
        const numericDepths = parsedMarkers
          .map(m => m.depth)
          .filter(d => typeof d === 'number' && !isNaN(d));
        if (numericDepths.length > 0) {
          const min = Math.min(...numericDepths);
          const max = Math.max(...numericDepths);
          setDepthRange([min, max]);
          setSelectedDepthRange([min, max]);
        }

      } catch (error) {
        console.error('Error loading Excel file:', error);
      }
    };
    loadExcel();
  }, []);

  // Functions for the dropdown content timeout
  const handleDropdownEnter = (name) => {
    clearTimeout(dropdownTimeouts.current[name]);
    setActiveDropdown(name);
  };
  const handleDropdownLeave = (name) => {
    dropdownTimeouts.current[name] = setTimeout(() => {
    setActiveDropdown(prev => (prev === name ? null : prev));
    }, 200); // 0.2s delay
  };



  return ( // Outputing the page
    <>
      <div className='map-page'>
        <Header />
          <div className='map-main-content'>
            <h2 className='map-title'>Bezerra <i>et al.</i>, 2025.</h2>
            <p className='map-legend'>
            <b>Figure 5:</b> Type location of the valid species of <i>Halalaimus</i> with addition of species affins.
            </p>

            <div className='dropdown-menu'>

              <div className='dropdown' // ID Dropdown
                onMouseEnter={() => handleDropdownEnter('id')}
                onMouseLeave={() => handleDropdownLeave('id')}
                >
                <button className='dropdown-button'>ID</button>
                <div className={`dropdown-content ${activeDropdown === 'id' ? 'show' : ''}`}>
                  <input
                    type='text'
                    className = 'search-field'
                    placeholder='Search ID...'
                    value={idSearch}
                    onChange={(e) => setIdSearch(e.target.value)}
                    style={{width:idInputWidth}}
                  />
                  {IdList.filter(id => id.toLowerCase().includes(idSearch.toLowerCase()))
                    .map((id, index) => (
                      <label key={index} className='custom-checkbox'>
                        <input
                          type='checkbox'
                          value={id}
                          checked={selectedIds.includes(id)}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSelectedIds(prev =>
                              prev.includes(value)
                                ? prev.filter(s => s !== value)
                                : [...prev, value]
                            );
                          }}
                        />
                        <span className='checkmark'></span>
                        <span style={{ marginLeft: '6px' }}>{id}</span>
                      </label>
                    ))}
                </div>
              </div>

              <div className='dropdown' // Species Dropdown
                onMouseEnter={() => handleDropdownEnter('species')}
                onMouseLeave={() => handleDropdownLeave('species')}
                >
                <button className='dropdown-button'>Species</button>
                <div className={`dropdown-content ${activeDropdown === 'species' ? 'show' : ''}`}>
                  <input
                    type='text'
                    className = 'search-field'
                    placeholder='Search Species...'
                    value={speciesSearch}
                    onChange={(e) => setSpeciesSearch(e.target.value)}
                    style={{width:speciesInputWidth}}
                  />
                  {speciesList.filter(species => species.toLowerCase().includes(speciesSearch.toLowerCase()))
                    .map((species, index) => (
                      <label key={index} className='custom-checkbox'>
                        <input
                          type='checkbox'
                          value={species}
                          checked={selectedSpecies.includes(species)}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSelectedSpecies(prev =>
                            prev.includes(value)
                              ? prev.filter(s => s !== value)
                              : [...prev, value]
                            );
                          }}
                        />
                        <span className='checkmark'></span>
                        <span style={{ marginLeft: '6px' }}><i>{species}</i></span>
                      </label>
                    ))}
                </div>
              </div>

              <div className='dropdown' // Habitat Dropdown
                onMouseEnter={() => handleDropdownEnter('habitat')}
                onMouseLeave={() => handleDropdownLeave('habitat')}
                >
                <button className='dropdown-button'>Habitat</button>
                <div className={`dropdown-content ${activeDropdown === 'habitat' ? 'show' : ''}`}>
                  {['Marine', 'Brackish', 'Fresh'].map((habitat) => (
                    <label key={habitat} className='custom-checkbox'>
                      <input
                        type='checkbox'
                        name={habitat}
                        checked={habitatFilters[habitat]}
                        onChange={(e) => {
                          const { name, checked } = e.target;
                          setHabitatFilters(prev => ({ ...prev, [name]: checked }));
                        }}
                      />
                      <span className='checkmark'></span>
                      <span style={{ marginLeft: '6px' }}>{habitat}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className='dropdown' // Is Deep-Sea Dropdown
                onMouseEnter={() => handleDropdownEnter('isdeepsea')}
                onMouseLeave={() => handleDropdownLeave('isdeepsea')}
                >
                <button className='dropdown-button'>Is Deep-Sea</button>
                <div className={`dropdown-content ${activeDropdown === 'isdeepsea' ? 'show' : ''}`}>
                  {IsDeepSeaList.map((isdeepsea, index) => (
                    <label key={index} className='custom-checkbox'>
                      <input
                        type='checkbox'
                        value={isdeepsea}
                        checked={selectedIsDeepSea.includes(isdeepsea)}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSelectedIsDeepSea(prev =>
                            prev.includes(value)
                              ? prev.filter(s => s !== value)
                              : [...prev, value]
                          );
                        }}
                      />
                      <span className='checkmark'></span>
                      <span style={{ marginLeft: '6px' }}><i>{isdeepsea}</i></span>
                    </label>
                  ))}
                </div>
              </div>

              <div className='dropdown' // Is Description Article Dropdown
                onMouseEnter={() => handleDropdownEnter('isdescription')}
                onMouseLeave={() => handleDropdownLeave('isdescription')}
                >
                <button className='dropdown-button'>Is Description Specimen</button>
                <div className={`dropdown-content ${activeDropdown === 'isdescription' ? 'show' : ''}`}>
                  {IsDescriptionList.map((isdescription, index) => (
                    <label key={index} className='custom-checkbox'>
                      <input
                        type='checkbox'
                        value={isdescription}
                        checked={selectedIsDescription.includes(isdescription)}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSelectedIsDescription(prev =>
                            prev.includes(value)
                              ? prev.filter(s => s !== value)
                              : [...prev, value]
                          );
                        }}
                      />
                      <span className='checkmark'></span>
                      <span style={{ marginLeft: '6px' }}><i>{isdescription}</i></span>
                    </label>
                  ))}
                </div>
              </div>

              <div className='dropdown' // Description Article Dropdown
                onMouseEnter={() => handleDropdownEnter('description')}
                onMouseLeave={() => handleDropdownLeave('description')}
                >
                <button className='dropdown-button'>Description Article</button>
                <div className={`dropdown-content ${activeDropdown === 'description' ? 'show' : ''}`}>
                  <input
                    type='text'
                    className = 'search-field'
                    placeholder='Search Articles...'
                    value={descriptionSearch}
                    onChange={(e) => setDescriptionSearch(e.target.value)}
                    style={{width:descriptionInputWidth}}
                  />
                  {DescriptionList.filter(description => description.toLowerCase().includes(descriptionSearch.toLowerCase()))
                    .map((description, index) => (
                      <label key={index} className='custom-checkbox'>
                        <input
                          type='checkbox'
                          value={description}
                          checked={selectedDescription.includes(description)}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSelectedDescription(prev =>
                            prev.includes(value)
                              ? prev.filter(s => s !== value)
                              : [...prev, value]
                          );
                        }}
                      />
                      <span className='checkmark'></span>
                      <span style={{ marginLeft: '6px' }}>{description}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className='dropdown' // Sighting  Article Dropdown
                onMouseEnter={() => handleDropdownEnter('sighting')}
                onMouseLeave={() => handleDropdownLeave('sighting')}
                >
                <button className='dropdown-button'>Sighting Reference</button>
                <div className={`dropdown-content ${activeDropdown === 'sighting' ? 'show' : ''}`}>
                  <input
                    type='text'
                    className = 'search-field'
                    placeholder='Search Articles...'
                    value={sightingSearch}
                    onChange={(e) => setSightingSearch(e.target.value)}
                    style={{width:sightingInputWidth}}
                  />
                  {SightingList.filter(sighting => sighting.toLowerCase().includes(sightingSearch.toLowerCase()))
                    .map((sighting, index) => (
                      <label key={index} className='custom-checkbox'>
                        <input
                          type='checkbox'
                          value={sighting}
                          checked={selectedSighting.includes(sighting)}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSelectedSighting(prev =>
                            prev.includes(value)
                              ? prev.filter(s => s !== value)
                              : [...prev, value]
                          );
                        }}
                      />
                      <span className='checkmark'></span>
                      <span style={{ marginLeft: '6px' }}>{sighting}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className='slider-container' // Depth Slider
              >
                <label><b>Depth Range (m):</b></label>
                <ReactSlider
                  className='depth-range-slider'
                  thumbClassName='depth-thumb'
                  trackClassName='depth-track'
                  renderTrack={(props, state) => {
                    const { key, ...rest } = props;
                    return (
                      <div
                        key={key}
                        {...rest}
                        className={`depth-track${state.index === 1 ? ' depth-track--active' : ''}`}
                      />
                    );
                  }}
                  min={depthRange[0]}
                  max={depthRange[1]}
                  value={selectedDepthRange}
                  onChange={(value) => setSelectedDepthRange(value)}
                  step={0.1}
                  minDistance={1}
                  pearling
                />
                <div className='depth-values'>
                  <span>{selectedDepthRange[0]} m</span> – <span>{selectedDepthRange[1]} m</span>
                </div>
              </div>

            </div>



            <MapContainer className='map-container' // Dynamic leaflet map
              center={[0, 0]}
              zoom={2} minZoom={2} maxZoom={18}
              maxBounds={[[-90, -300],[90, 300]]}
              maxBoundsViscosity={0.1}>
                
              <TileLayer
                url='https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
                attribution='&copy; Esri'
              />
              <LayersControl position='topright'>

                <LayersControl.Overlay name='Active Exploration Areas' // Active Reserved Areas
                >
                  <LayerGroup>
                  <EsriFeatureLayer
                    url='https://services5.arcgis.com/VcAAb5oBhdAAnFj2/arcgis/rest/services/fclContractAreas_20240724/FeatureServer/32'
                    style={{color: 'red', weight: 0.5, fillOpacity: 0.2}}
                    popupFields={['ContractID']}
                  />
                  </LayerGroup>
                </LayersControl.Overlay>
                
                <LayersControl.Overlay name='Active Reserved Areas' // Active Reserved Areas
                >
                  <LayerGroup>
                  <EsriFeatureLayer
                    url='https://services5.arcgis.com/VcAAb5oBhdAAnFj2/arcgis/rest/services/fclContractAreas_20240724/FeatureServer/33'
                    style={{color: 'orange', weight: 0.5, fillOpacity: 0.2}}
                    popupFields={['ContractID']}
                  />
                  </LayerGroup>
                </LayersControl.Overlay>

                <LayersControl.Overlay name='Areas of Particular Environmental Interest' // Areas of Particular Environmental Interest (APEIs)
                >
                  <LayerGroup>
                  <EsriFeatureLayer
                    url='https://services5.arcgis.com/VcAAb5oBhdAAnFj2/arcgis/rest/services/fclContractAreas_20240724/FeatureServer/35'
                    style={{color: 'white', weight: 0.5, fillOpacity: 0.3}}
                    popupFields={['Remarks']}
                  />
                  </LayerGroup>
                </LayersControl.Overlay>

              </LayersControl>

              {markers // filtering map according to selected options on dropdown-menu
                .filter(marker =>
                  (selectedIds.length === 0 || selectedIds.includes(marker.id)) &&
                  (selectedSpecies.length === 0 || selectedSpecies.includes(marker.species)) &&
                  (selectedHabitats.length === 0 || selectedHabitats.some(h => marker[h.toLowerCase()] === true || marker[h.toLowerCase()] === 'TRUE')) &&
                  (selectedIsDeepSea.length === 0 || selectedIsDeepSea.includes(marker.isdeepsea)) &&
                  (selectedIsDescription.length === 0 || selectedIsDescription.includes(marker.isdescription)) &&
                  (selectedDescription.length === 0 || selectedDescription.includes(marker.description)) &&
                  (selectedSighting.length === 0 || selectedSighting.includes(marker.sighting)) &&
                  (marker.depth === null || (
                    marker.depth >= selectedDepthRange[0] &&
                    marker.depth <= selectedDepthRange[1]
                  ))
                )
                .map(marker => ( // Plotting filtered markers on leaflet map
                <Marker
                  key={`${marker.id}-${marker.position.join(',')}`}
                  position={marker.position}
                  icon={L.divIcon({
                    className: 'map-markers',
                    iconSize: [7, 7],       // set marker size here
                    iconAnchor: [3.5, 3.5]  // center markers on half their width and height
                  })}>

                  <Popup>
                    <div>
                      <p><b>Occurrence Nº:</b> {marker.id}</p>
                      <p><b>Species: </b><i>{marker.species}</i></p>
                      <p><b>Country: </b>{marker.country}</p>
                      <p><b>Location: </b>{marker.location}</p>
                      <p><b>Coordinates: </b>{marker.coordinates}</p>
                      <p><b>Depth: </b>{marker.depth !== null ? `${marker.depth} m` : 'Unknown'}</p>
                      <p><b>Is Description Specimen: </b>{marker.isdescription}</p>
                      <p><b>Reference: </b> {marker.sightingLink
                        ? (
                            <a href={marker.sightingLink} target="_blank" rel="noopener noreferrer">
                              {marker.sighting}
                            </a>
                          ) : (marker.sighting)}
                      </p>
                    </div>
                  </Popup>
                </Marker>
                ))}

              <MapZoomController
                markers={markers.filter(marker =>
                  (selectedIds.length === 0 || selectedIds.includes(marker.id)) &&
                  (selectedSpecies.length === 0 || selectedSpecies.includes(marker.species)) &&
                  (selectedHabitats.length === 0 || selectedHabitats.some(h => marker[h.toLowerCase()] === true || marker[h.toLowerCase()] === 'TRUE')) &&
                  (selectedIsDeepSea.length === 0 || selectedIsDeepSea.includes(marker.isdeepsea)) &&
                  (selectedIsDescription.length === 0 || selectedIsDescription.includes(marker.isdescription)) &&
                  (selectedDescription.length === 0 || selectedDescription.includes(marker.description)) &&
                  (selectedSighting.length === 0 || selectedSighting.includes(marker.sighting)) &&
                  (marker.depth === null || (
                    marker.depth >= selectedDepthRange[0] &&
                    marker.depth <= selectedDepthRange[1]
                  ))
                )}
              />
            </MapContainer>

          </div>
          <a className='data-link' href='/NematodeMapData'>View/Download the data.</a>
        <Footer />
      </div>
    </>
  );
};

export default NematodeMap;